import { Area } from 'react-easy-crop/types';

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.setAttribute('crossOrigin', 'anonymous');
    image.onload = () => resolve(image);
    image.onerror = (error) => reject(error);
    image.src = url; // Pode ser dataURL ou URL remota
  });

/**
 * Detecta MIME e extensão a partir do dataURL.
 * - Se `imageSrc` começa com "data:image/png", retorna "image/png" e ".png".
 * - Se "data:image/gif", retorna "image/gif" e ".gif".
 * - Se "data:image/jpeg", retorna "image/jpeg" e ".jpg".
 * - Caso não detecte, assume JPEG por padrão.
 */
function detectMimeAndExtension(imageSrc: string): { mime: string; ext: string } {
  // Exemplo simples, só detectando PNG/JPEG/GIF
  if (imageSrc.startsWith('data:image/png')) {
    return { mime: 'image/png', ext: '.png' };
  }
  if (imageSrc.startsWith('data:image/gif')) {
    return { mime: 'image/gif', ext: '.gif' };
  }
  // Se contiver "jpeg" ou "jpg"
  if (imageSrc.startsWith('data:image/jpeg') || imageSrc.startsWith('data:image/jpg')) {
    return { mime: 'image/jpeg', ext: '.jpg' };
  }
  // Caso não detecte, forçamos JPEG como fallback
  return { mime: 'image/jpeg', ext: '.jpg' };
}

const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<File> => {
  // Cria <img>
  const image = await createImage(imageSrc);

  // Cria <canvas> do tamanho do recorte
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Não foi possível obter o contexto 2D do canvas');
  }

  // Desenha no canvas a região de recorte
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Detecta o mime/extension a partir do imageSrc
  const { mime, ext } = detectMimeAndExtension(imageSrc);

  // Converter o conteúdo do canvas em Blob e depois em File
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas está vazio ou não foi gerado o Blob.'));
        return;
      }
      // Converter Blob em File
      const file = new File([blob], `cropped_image${ext}`, {
        type: mime,
        lastModified: Date.now(),
      });
      resolve(file);
    }, mime);
  });
};

export default getCroppedImg;